class QuantityBundle {
  constructor(container) {
    this.container = container;
    this.buyButton = container.querySelector(".quantity-bundle__buy-button");
    this.radioButtons = container.querySelectorAll(".quantity-bundle__radio");
    this.comparePriceEl = container.querySelector(
      ".quantity-bundle__compare-price"
    );
    this.currentPriceEl = container.querySelector(
      ".quantity-bundle__current-price"
    );
    this.pixPriceEl = container.querySelector(".quantity-bundle__pix-price");
    this.variantSteppers = container.querySelectorAll(
      ".variant-selector__stepper-button"
    );
    this.variantInputs = container.querySelectorAll(
      ".variant-selector__quantity-input"
    );
    this.variantAddButtons = container.querySelectorAll(
      ".variant-selector__add-button"
    );
    this.progressFill = container.querySelector(
      ".quantity-bundle__progress-fill"
    );
    this.progressOptions = container.querySelectorAll(
      ".quantity-bundle__option"
    );

    this.baseComparePrice =
      parseFloat(this.comparePriceEl.dataset.comparePrice) / 100 || 0;
    this.baseCurrentPrice =
      parseFloat(this.currentPriceEl.dataset.currentPrice) / 100 || 0;
    this.pixDiscountPercent =
      parseFloat(this.pixPriceEl.dataset.pixDiscount) || 1;

    // Threshold da assinatura em constante. Altere aqui pra testar valores diferentes.
    this.SUBSCRIPTION_THRESHOLD = 20;

    // normalizador
    this._norm = (s) =>
      (s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    // nome exibido -> chave interna
    this.FLAVOR_KEY_BY_NAME = {
      [this._norm("Cappuccino - Cafeína")]: "cappuccino",
      [this._norm("Limão")]: "limao",
      [this._norm("Neutro")]: "neutro",
      [this._norm("Freeze")]: "freeze",
      [this._norm("Tropical")]: "tropical",
      [this._norm("Sweet Banana - Cafeína")]: "sweet",
    };

    // receitas por kit
    this.KIT_RECIPES = {
      high: {
        6: { sweet: 2, tropical: 1, neutro: 1, cappuccino: 1, freeze: 1 },
        12: { sweet: 3, tropical: 3, cappuccino: 2, neutro: 2, freeze: 2 },
        20: { sweet: 5, tropical: 4, neutro: 4, cappuccino: 4, freeze: 3 },
        30: { sweet: 7, tropical: 6, cappuccino: 6, neutro: 6, freeze: 5 },
        50: { sweet: 12, tropical: 10, cappuccino: 10, neutro: 10, freeze: 8 },
      },
      low: {
        6: { limao: 2, freeze: 2, tropical: 1, neutro: 1 },
        12: { limao: 3, freeze: 3, tropical: 4, neutro: 2 },
        20: { limao: 5, freeze: 5, tropical: 6, neutro: 4 },
        30: { limao: 7, freeze: 7, tropical: 10, neutro: 6 },
        50: { limao: 12, freeze: 12, tropical: 16, neutro: 10 },
      },
      default: {
        6: { limao: 0, freeze: 0, tropical: 0, neutro: 0 }
      }
    };

    // Cria um mapa de descontos baseado na quantidade
    this.discountMap = {};
    const hiddenDiv = this.container.querySelector("[data-discount-map]");
    if (hiddenDiv && hiddenDiv.dataset.discountMap) {
      const discountMapStr = hiddenDiv.dataset.discountMap;
      discountMapStr.split(",").forEach((entry) => {
        const [quantity, discount] = entry.split(":");
        this.discountMap[parseInt(quantity)] = parseFloat(discount);
      });
    }

    this.bundleConfigs = [];
    this.container
      .querySelectorAll(".quantity-bundle__option")
      .forEach((option) => {
        const quantity = parseInt(option.dataset.quantity);
        if (quantity) {
          const discountValue = this.discountMap[quantity] || 0;

          this.bundleConfigs.push({
            quantity,
            discount: discountValue,
            radio: option.querySelector(".quantity-bundle__radio"),
            element: option,
          });
        }
      });

    this.bundleConfigs.sort((a, b) => a.quantity - b.quantity);

    this.variantQuantities = {};
    this.totalVariantQuantity = 0;

    this.init();
  }

  init() {
    this.setupInitialState();
    this.bindEvents();
    this.setupButton();
    this.updateAll();
  }

  setupInitialState() {
    const size = parseInt(window.kit || 6, 10);
    const urlParams = new URLSearchParams(window.location.search);
    const respostasParam = urlParams.get('respostas') || '';

    let profileKey = parseInt(window.suor ?? 1, 10) >= 2 ? "high" : "low";
 
    if (respostasParam == '') {
      profileKey = "default";
      return;
    }

    const recipe = this.KIT_RECIPES?.[profileKey]?.[size] || {};

    const inputs = Array.from(this.variantInputs || []);
    let totalSet = 0;
    const avail = [];

    inputs.forEach((input) => {
      const parent = input.closest(".variant-selector__option");
      if (!parent || parent.dataset.available !== "true") return;

      const variantId = input.dataset.variantId;
      const name = (
        parent.querySelector(".variant-selector__name")?.textContent || ""
      ).trim();
      const key = this.FLAVOR_KEY_BY_NAME?.[this._norm(name)];
      const qty = key && recipe[key] != null ? recipe[key] : 0;

      this.variantQuantities[variantId] = qty;
      input.value = qty;
      this.updateVariantDisplay(variantId, qty);

      totalSet += qty;
      avail.push({ id: variantId, input });
    });

    let remaining = Math.max(0, size - totalSet);
    for (let i = 0; i < remaining && avail.length > 0; i++) {
      const e = avail[i % avail.length];
      const next = parseInt(e.input.value || "0", 10) + 1;
      e.input.value = next;
      this.variantQuantities[e.id] = next;
      this.updateVariantDisplay(e.id, next);
    }

    this.totalVariantQuantity = size;
  }

  setupButton() {
    this.originalButtonText = this.buyButton.textContent.trim();
    this.originalButtonStyle = this.buyButton.style.background;
  }

  // Publica um custom event (paceit:view_variant) via lib do Shopify,
  // capturável por um Web Pixel em Customer Events. Best-effort: qualquer
  // falha aqui é silenciada para não impactar o fluxo de seleção da variante.
  /** @param {HTMLElement} button */
  trackViewVariant(button) {
    try {
      const parentOption = button.closest(".variant-selector__option");
      const input = /** @type {HTMLElement | null} */ (
        parentOption
          ? parentOption.querySelector(".variant-selector__quantity-input")
          : null
      );

      const productId = button.dataset.productId || "";
      const productName = button.dataset.productName || "";
      const sku = button.dataset.sku || "";
      // data-variant-price vem em centavos; GA4 espera o valor decimal.
      const priceCents = button.dataset.variantPrice || "0";
      const priceMoney = button.dataset.variantPriceMoney || "R$ 0";
      const priceCast = (parseFloat(priceCents) || 0) / 100;
      
      // const price = (parseFloat(button.dataset.variantPrice || "0") || 0) / 100;
      const variantId = button.dataset.variantId || "";
      const variantName = input ? input.dataset.variantName || "" : "";


      /*
        Event Data (ecomm_pagetype)
          cart
          Event Data (ecomm_prodid)
          shopify_ZZ_8360896725129_44996988895369
          Event Data (ecomm_totalvalue)
          14.99
          Event Data (value)
          14.99
          Currency Code
          BRL
      */ 

      const w = /** @type {any} */ (window);
      // Publica via lib do Shopify; os dados chegam ao pixel em event.customData.
      const viewVariantPayload = {
          currency: "BRL",
          value: priceCast,
          items: [
            {
              item_shopify_id: `shopify_ZZ_${productId}_${variantId}`,
              item_product_id: productId,
              item_variant_id: variantId,
              item_sku: sku,
              item_name: `${productName} - ${variantName}`,
              item_product_name: productName,
              item_variant_name: variantName,
              price: priceCast,
              price_money: priceMoney,
              price_cents: priceCents,
              quantity: 1,
            },
          ],
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "view_variant", ...viewVariantPayload });

        if (Shopify && Shopify.analytics && typeof Shopify.analytics.publish === "function") {
          Shopify.analytics.publish("view_variant", viewVariantPayload);
        }
    } catch (err) {
      console.warn("viewVariant publish falhou:", err);
    }
  }

  bindEvents() {
    this.variantAddButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        const variantId = button.dataset.variantId;

        // Dispara o evento de visualização da variante (GA4 view_item)
        this.trackViewVariant(button);

        this.variantQuantities[variantId] = 1;
        this.updateVariantDisplay(variantId, 1);
        this.updateAll();
      });
    });

    this.variantSteppers.forEach((button) => {
      button.addEventListener("click", () => {
        const input = button.parentElement.querySelector(
          ".variant-selector__quantity-input"
        );
        const variantId = input.dataset.variantId;
        const step = parseInt(button.dataset.step);
        let newValue = (parseInt(input.value) || 0) + step;
        if (newValue < 0) newValue = 0;

        this.variantQuantities[variantId] = newValue;
        this.updateVariantDisplay(variantId, newValue);
        this.updateAll();
      });
    });

    this.variantInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const variantId = e.target.dataset.variantId;
        const newValue = parseInt(e.target.value) || 0;
        this.variantQuantities[variantId] = newValue;
        this.updateVariantDisplay(variantId, newValue);
        this.updateAll();
      });
    });

    if(this.buyButton) {
      this.buyButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  updateAll() {
    this.updateTotalVariantQuantity();
    this.updateProgressBar();
    this.updatePricesExtraProducts();
    this.validateState();
  }

  updateTotalVariantQuantity() {
    this.totalVariantQuantity = Object.values(this.variantQuantities).reduce(
      (sum, qty) => sum + qty,
      0
    );
  }

  updateExtraProductVariantData(variantName, quantity) {
    const inputs = document.querySelectorAll(
      `.extra-product-variants[data-variant-name="${variantName}"]`
    );
    if (!inputs.length) return;

    inputs.forEach((el) => el.setAttribute("data-variant-qtd", quantity));

    this.updatePricesExtraProducts();
  }

  updateVariantDisplay(variantId, quantity) {
    const parentOption = this.container
      .querySelector(`[data-variant-id="${variantId}"]`)
      .closest(".variant-selector__option");
    const addButton = parentOption.querySelector(
      ".variant-selector__add-button"
    );
    const controls = parentOption.querySelector(".variant-selector__controls");
    const input = parentOption.querySelector(
      ".variant-selector__quantity-input"
    );

    input.value = quantity;
    if (input.dataset.variantName) {
      this.updateExtraProductVariantData(input.dataset.variantName, quantity);
    }

    if (quantity === 0) {
      addButton.style.setProperty("display", "block", "important");
      controls.style.setProperty("display", "none", "important");
    } else {
      addButton.style.setProperty("display", "none", "important");
      controls.style.setProperty("display", "flex", "important");
    }
  }

  updateBundleSelection() {
    const sortedConfigs = [...this.bundleConfigs].sort(
      (a, b) => a.quantity - b.quantity
    );

    let bestBundle = null;
    for (let i = sortedConfigs.length - 1; i >= 0; i--) {
      if (this.totalVariantQuantity >= sortedConfigs[i].quantity) {
        bestBundle = sortedConfigs[i];
        break;
      }
    }

    if (
      !bestBundle &&
      this.totalVariantQuantity > 0 &&
      sortedConfigs.length > 0
    ) {
      bestBundle = sortedConfigs[0];
    }

    this.radioButtons.forEach((radio) => (radio.checked = false));
    if (bestBundle && bestBundle.radio) {
      bestBundle.radio.checked = true;
    }

    return bestBundle;
  }

  updateProgressBar() {
    const currentQuantity = this.totalVariantQuantity;

    const sortedOptions = Array.from(this.progressOptions).sort((a, b) => {
      return parseInt(a.dataset.quantity) - parseInt(b.dataset.quantity);
    });

    const steps = sortedOptions.map((opt) => parseInt(opt.dataset.quantity));
    const minQuantity = steps[0];
    const maxQuantity = steps[steps.length - 1];

    let progressPercentage = 0;

    if (currentQuantity > 0) {
      if (currentQuantity >= maxQuantity) {
        progressPercentage = 100;
      } else {
        let prevStep = minQuantity;
        let nextStep = maxQuantity;
        let prevIndex = 0;

        for (let i = 0; i < steps.length; i++) {
          if (currentQuantity < steps[i]) {
            nextStep = steps[i];
            prevStep = steps[i - 1] || minQuantity;
            prevIndex = i - 1;
            break;
          }
        }

        const segment = steps.length > 1 ? 100 / (steps.length - 1) : 100;
        const base = prevIndex * segment;

        const segmentProgress =
          (currentQuantity - prevStep) / (nextStep - prevStep);
        progressPercentage = base + segmentProgress * segment;

        if (currentQuantity < minQuantity) {
          progressPercentage = Math.min(
            15,
            (currentQuantity / minQuantity) * 15
          );
        }
      }
    }

    if (this.progressFill) {
      this.progressFill.style.width = `${progressPercentage}%`;
    }

    sortedOptions.forEach((option, index) => {
      const optionQuantity = steps[index];
      option.classList.remove("is-completed");
      if (currentQuantity >= optionQuantity) {
        option.classList.add("is-completed");
      }
    });
  }

  updatePrices() {
    const quantity = this.totalVariantQuantity;
    if (quantity === 0) {
      this.currentPriceEl.textContent = this.formatPrice(0);
      this.comparePriceEl.textContent = "";
      this.pixPriceEl.style.display = "none";
      return;
    }

    const selectedBundle = this.updateBundleSelection();
    const discount = selectedBundle ? selectedBundle.discount / 100 : 0;

    const totalCompare = this.baseComparePrice * quantity;

    const discountedPricePerItem =
      Math.ceil(this.baseCurrentPrice * (1 - discount) * 100) / 100;
    const totalCurrent = discountedPricePerItem * quantity;
    const totalPix = totalCurrent * (1 - this.pixDiscountPercent / 100);

    this.comparePriceEl.textContent = this.formatPrice(totalCompare);
    this.currentPriceEl.textContent = this.formatPrice(totalCurrent);

    this.pixPriceEl.innerHTML = `${this.formatPrice(totalPix)} ${
      this.pixPriceEl.dataset.pixText || "no pix"
    }`;
    this.pixPriceEl.style.display = "inline-block";
  }

  getQuantityDiscountMap() {
    const steps = document.querySelectorAll(".quantity-bundle__step");
    const result = {};

    steps.forEach((step) => {
      const qtyEl = step.querySelector(".quantity-bundle__step-number");
      const discountEl = step.querySelector(".quantity-bundle__step-discount");

      if (!qtyEl || !discountEl) return;

      const qty = qtyEl.textContent.replace(/\D+/g, "");
      const discount = discountEl.textContent.replace(/\D+/g, "");

      if (qty && discount) {
        result[qty] = discount;
      }
    });

    return result;
  }

  updatePricesExtraProducts() {
    const priceHTML = document.querySelector(".quantity-bundle__current-price");
    const pixHTML = document.querySelector(".quantity-bundle__pix-price");
    const optionPrices = document.querySelectorAll(".option-default-price");
    const globalQuantity = this.totalVariantQuantity;

    if (!priceHTML || !pixHTML) return;

    const pixDiscount = Number(pixHTML.dataset.pixDiscount || 0);
    const selectedCard = document.querySelector(
      ".option-card.selected .option-default-price"
    );
    if (!selectedCard) {
      this.updatePrices();
      this.updateSubscriptionLockState(globalQuantity);
      return;
    }

    const normalizePrice = (value) => {
      const num = Number(value || 0);
      return num < 1000 ? Math.round(num * 100) : num;
    };

    const discountsByQuantity = this.getQuantityDiscountMap();
    let appliedDiscount = 0;
    Object.keys(discountsByQuantity).forEach((minQty) => {
      if (globalQuantity >= Number(minQty)) {
        appliedDiscount = Number(discountsByQuantity[minQty]);
      }
    });

    const discountApplyDefault =
      normalizePrice(selectedCard.dataset.defaultPrice) *
      (1 - appliedDiscount / 100);
    const discountedPriceGlobal =
      Math.ceil(discountApplyDefault) * globalQuantity;
    priceHTML.innerText = this.formatPrice(discountedPriceGlobal / 100);

    const pixPriceGlobal =
      discountedPriceGlobal -
      discountedPriceGlobal * (pixDiscount / 100);
    pixHTML.innerText = `${this.formatPrice(pixPriceGlobal / 100)} no pix`;

    optionPrices.forEach((optionPrice) => {
      const defaultPrice = normalizePrice(optionPrice.dataset.defaultPrice);

      if (!globalQuantity) {
        optionPrice.innerText = this.formatPrice(defaultPrice / 100);
        return;
      }

      const discountedUnit = defaultPrice * (1 - appliedDiscount / 100);
      const optionTotal = Math.ceil(discountedUnit) * globalQuantity;
      optionPrice.innerText = this.formatPrice(optionTotal / 100);
    });

    const economizeText = document.querySelector(
      ".option-card.is-subscription .benefits ul li:first-child"
    );
    if (economizeText) {
      const parseBRL = (value) =>
        Number(
          value
            .replace(/\s/g, "")
            .replace("R$", "")
            .replace(".", "")
            .replace(",", ".")
        ) || 0;
      const pricesCompare = [...document.querySelectorAll(".option-default-price")]
        .map((el) => parseBRL(el.textContent));
      const resultCompare = Math.abs(
        pricesCompare.reduce((acc, price) => acc - price)
      );
      const formattedCompare = this.formatPrice(resultCompare);

      economizeText.innerHTML = `Economize ${formattedCompare}`;
    }

    this.updateSubscriptionLockState(globalQuantity);
  }

  /* ==============================================================
     Atualiza estado locked/unlocked do card de assinatura,
     o contador "Faltam X unidade(s)" com singular/plural,
     e a visibilidade da tag PIX (oculta quando assinatura selecionada).
  ============================================================== */
  updateSubscriptionLockState(globalQuantity) {
    const subsCard = document.querySelector('.option-card.is-subscription');
    if (!subsCard) return;

    const signatureVariant = subsCard.parentElement;
    if (!signatureVariant) return;

    const remaining = Math.max(0, this.SUBSCRIPTION_THRESHOLD - globalQuantity);
    const isUnlocked = globalQuantity >= this.SUBSCRIPTION_THRESHOLD;

    signatureVariant.style.display = "block";
    signatureVariant.dataset.locked = isUnlocked ? "false" : "true";

    // Atualiza o contador com tratamento de singular/plural
    const counterText = signatureVariant.querySelector(".subscription-progress__text");
    if (counterText) {
      const unidadeLabel = remaining === 1 ? "unidade" : "unidades";
      counterText.innerHTML = `Faltam <strong class="subscription-progress__count">${remaining}</strong> ${unidadeLabel} para assinar com 20% OFF`;
    }

    // Se acabou de travar (estava selected e agora locked), 
    // transfere seleção pra compra única
    if (!isUnlocked && subsCard.classList.contains('selected')) {
      const uniqueCard = document.querySelector('.option-card.option-second');
      if (uniqueCard) {
        subsCard.classList.remove('selected');
        const subsInput = subsCard.querySelector('input');
        if (subsInput) subsInput.checked = false;
        uniqueCard.classList.add('selected');
        const uniqueInput = uniqueCard.querySelector('input');
        if (uniqueInput) uniqueInput.checked = true;
      }
    }

    // Visibilidade da tag PIX
    // PIX é pagamento único; assinatura é recorrente — não faz sentido juntar.
    const pixHTML = document.querySelector(".quantity-bundle__pix-price");
    if (pixHTML) {
      if (subsCard.classList.contains('selected')) {
        pixHTML.style.display = 'none';
      } else if (this.totalVariantQuantity > 0) {
        pixHTML.style.display = 'inline-block';
      } else {
        pixHTML.style.display = 'none';
      }
    }
  }

  validateState() {
    const isEnabled = this.totalVariantQuantity > 0;
    this.buyButton.disabled = !isEnabled;
    this.buyButton.textContent = isEnabled
      ? this.originalButtonText
      : "Selecione um item";
  }

  formatPrice(price) {
    return `R$${price.toFixed(2).replace(".", ",")}`;
  }

  async addToCart() {
    if (this.buyButton.disabled) return;
    this.buyButton.disabled = true;
    this.buyButton.textContent = "Adicionando...";

    const items = Object.entries(this.variantQuantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({ id: parseInt(id), quantity }));

    if (items.length === 0) {
      this.showErrorFeedback("Nenhum item selecionado");
      return;
    }

    try {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      await this.updateAndOpenCart();
      this.showSuccessFeedback();
    } catch (error) {
      console.error("Erro no addToCart:", error);
      this.showErrorFeedback();
    }
  }

  async updateAndOpenCart() {
    const cartDrawer = document.querySelector("cart-drawer-component");
    if (!cartDrawer) return;

    const cartItemsComponent = cartDrawer.querySelector(
      ".cart-items-component"
    );
    const sectionId = cartItemsComponent
      ? cartItemsComponent.dataset.sectionId
      : null;
    if (!sectionId) return;

    const response = await fetch(`/?section_id=${sectionId}`);
    const newDrawerHTML = await response.text();

    const newDrawerContent = new DOMParser().parseFromString(
      newDrawerHTML,
      "text/html"
    );
    const newCartItemsHTML = newDrawerContent.querySelector(
      ".cart-items-component"
    ).innerHTML;
    const newCartBubble = newDrawerContent.querySelector(
      ".header-actions__action .cart-bubble"
    );

    if (cartItemsComponent) cartItemsComponent.innerHTML = newCartItemsHTML;

    const headerCartBubble = document.querySelector(
      ".header-actions__action .cart-bubble"
    );
    if (headerCartBubble && newCartBubble) {
      headerCartBubble.innerHTML = newCartBubble.innerHTML;
      headerCartBubble.setAttribute(
        "data-cart-count",
        newCartBubble.dataset.cartCount
      );
    }

    const cartStateResponse = await fetch("/cart.js");
    const cart = await cartStateResponse.json();
    cartDrawer
      .querySelector(".dialog-modal")
      .classList.toggle("cart-drawer--empty", cart.item_count === 0);

    if (typeof cartDrawer.open === "function") cartDrawer.open();
  }

  showSuccessFeedback() {
    this.buyButton.textContent = "✓ Adicionado!";
    this.buyButton.style.background = "#4CAF50";
    setTimeout(() => this.restoreButton(), 2000);
  }

  showErrorFeedback(message = "Erro!") {
    this.buyButton.textContent = message;
    this.buyButton.style.background = "#f44336";
    setTimeout(() => this.restoreButton(), 3000);
  }

  restoreButton() {
    this.buyButton.disabled = false;
    this.buyButton.style.background = this.originalButtonStyle || "";
    this.validateState();
  }
}

function initQuantityBundles() {
  document
    .querySelectorAll(".quantity-bundle:not(.js-initialized)")
    .forEach((bundle) => {
      bundle.classList.add("js-initialized");
      new QuantityBundle(bundle);
    });
}

document.addEventListener("DOMContentLoaded", initQuantityBundles);
document.addEventListener("shopify:section:load", initQuantityBundles);